export type ImageCompressionOptions = {
  enabled?: boolean;
  maxSizeMB?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  minQuality?: number;
};

export const DEFAULT_IMAGE_COMPRESSION = {
  enabled: true,
  maxSizeMB: 1,
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  minQuality: 0.5,
};

/** false preserves the original file. PNG keeps transparency; JPEG/WebP use quality. */
export async function compressImage(
  file: File,
  options: ImageCompressionOptions | false = {},
): Promise<File> {
  const config = { ...DEFAULT_IMAGE_COMPRESSION, ...(options || {}) };
  if (options === false || !config.enabled) return file;
  if (
    ![config.maxSizeMB, config.maxWidth, config.maxHeight].every(
      (value) => Number.isFinite(value) && value > 0,
    ) ||
    ![config.quality, config.minQuality].every(
      (value) => Number.isFinite(value) && value > 0 && value <= 1,
    ) ||
    config.minQuality > config.quality ||
    config.maxWidth < 1 ||
    config.maxHeight < 1
  )
    throw new Error('Cấu hình nén ảnh không hợp lệ.');

  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Vui lòng chọn ảnh JPEG, PNG hoặc WebP khi bật nén ảnh.');
  }
  const maxBytes = Math.floor(config.maxSizeMB * 1024 * 1024);
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Không thể đọc ảnh đã chọn.'));
      element.src = url;
    });
    if (!img.naturalWidth || !img.naturalHeight)
      throw new Error('Ảnh không hợp lệ.');
    const ratio = Math.min(
      1,
      config.maxWidth / img.naturalWidth,
      config.maxHeight / img.naturalHeight,
    );
    // Keep small originals intact, avoiding unnecessary loss of quality.
    if (ratio === 1 && file.size <= maxBytes) return file;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Trình duyệt không hỗ trợ nén ảnh.');
    let width = Math.max(1, Math.floor(img.naturalWidth * ratio));
    let height = Math.max(1, Math.floor(img.naturalHeight * ratio));
    let quality = config.quality;
    // Bound encoding work even for impossible byte limits.
    for (let attempt = 0; attempt < 40; attempt += 1) {
      canvas.width = width;
      canvas.height = height;
      context.drawImage(img, 0, 0, width, height);
      const encodingQuality = quality;
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) =>
            result ? resolve(result) : reject(new Error('Không thể nén ảnh.')),
          file.type,
          encodingQuality,
        );
      });
      if (blob.size <= maxBytes) {
        const extension = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
        }[blob.type];
        if (!extension)
          throw new Error('Định dạng ảnh đầu ra không được hỗ trợ.');
        return new File(
          [blob],
          `${file.name.replace(/\.[^.]+$/, '')}.${extension}`,
          {
            type: blob.type,
            lastModified: file.lastModified,
          },
        );
      }
      if (blob.type !== 'image/png' && quality > config.minQuality) {
        quality = Math.max(config.minQuality, quality - 0.1);
      } else {
        if (width === 1 && height === 1) break;
        width = Math.max(1, Math.floor(width * 0.8));
        height = Math.max(1, Math.floor(height * 0.8));
      }
    }
    throw new Error(
      `Không thể giảm ảnh xuống giới hạn ${config.maxSizeMB} MB. Vui lòng chọn ảnh khác.`,
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
